<template>
  <div class="container-fluid mt-4">

    <!-- ===================== CARDS ===================== -->
    <div class="row mb-4">
      <!-- NPS Geral -->
      <div class="col-md-3">
        <div class="card card-nps shadow-sm">
          <div class="card-body">
            <h6 class="text-muted">NPS Geral</h6>
            <h1 class="text-nps-passive font-weight-bold">{{ nps_general }}</h1>
            <small class="text-muted">Score consolidado</small>
          </div>
        </div>
      </div>

      <!-- Respondentes -->
      <div class="col-md-3">
        <div class="card shadow-sm">
          <div class="card-body">
            <h6 class="text-muted">Respondentes</h6>
            <h1 class="text-foreground font-weight-bold">{{ respondentes }}</h1>
            <small class="text-muted">{{ total_respostas }} respostas totais</small>
          </div>
        </div>
      </div>

      <!-- Promotores -->
      <div class="col-md-3">
        <div class="card shadow-sm">
          <div class="card-body">
            <h6 class="text-muted text-success">Promotores</h6>
            <h1 class="font-weight-bold text-success">{{ promotores.percentual }}</h1>
            <small class="text-muted">{{ promotores.quantidade }} respostas (9-10)</small>
          </div>
        </div>
      </div>

      <!-- Detratores -->
      <div class="col-md-3">
        <div class="card shadow-sm">
          <div class="card-body">
            <h6 class="text-muted text-danger">Detratores</h6>
            <h1 class="font-weight-bold text-danger">{{ detratores.percentual }}</h1>
            <small class="text-muted">{{ detratores.quantidade }} respostas (1-6)</small>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== TABELA ===================== -->

    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="text-foreground mb-0">Tabela de Respostas</h5>

        <div class="d-flex align-items-center ml-auto">
          <b-button
            id="legend-btn"
            variant="light"
            v-b-popover.hover.right="'Clique para ver legenda'"
          >
            Legenda das Perguntas
          </b-button>

          <b-button variant="success" @click="exportExcel" class="ml-5">
            Exportar Excel
          </b-button>

          <b-form-input
            v-model="filterAnswer"
            placeholder="Pesquisar..."
            class="ml-2 w-25"
          ></b-form-input>
        </div>
      </div>

      <div class="card-body">
        <b-table
          class="nps-table"
          :items="respostas"
          :fields="fields"
          :filter="filterAnswer"
          :filter-function="customFilterAnswer"
          striped
          hover
          responsive
          borderless
        >
          <!-- Coluna Classificação -->
          <template #cell(classificacao)="data">
            <b-badge
              :variant="badgeVariant(data.item.nota)"
              pill
            >
              {{ classificar(data.item.nota) }}
            </b-badge>
          </template>
        </b-table>

        <div class="d-flex justify-content-end mt-3">
            <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                align="right"
                size="sm"
                class="mt-3"
            />
        </div>
      </div>
    </div>

    <b-popover
      target="legend-btn"
      triggers="click"
      placement="right"
      custom-class="popover-legend"
    >
      <template #title>
        Legenda com categoria
      </template>

      <b-table
        :items="legenda"
        :fields="fieldsLegenda"
        small
        responsive
      >
        <template #cell(categoria)="data">
          <b-badge variant="secondary">
            {{ data.item.categoria }}
          </b-badge>
        </template>
      </b-table>
    </b-popover>

  </div>
</template>

<script>
import * as XLSX from 'xlsx';

export default {
  name: "NpsSurveyDashboard",
  props: ['filters'],

  data() {
    return {
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      nps_general: 0,
      respondentes: 0,
      total_respostas: 0,
      promotores: {},
      detratores: {},
      filterAnswer: '',

      fields: [
        { 
            key: "campaign_date_sent", 
            label: "Data de envio",
            thStyle: { width: '150px' },
            sortable: true,
            formatter: (value) => value ? moment(value).format('DD/MM/YYYY HH:mm') : '—'
        },
        { 
            key: "created_at", 
            label: "Data da resposta",
            thStyle: { width: '160px' },
            sortable: true,
            formatter: (value) => value ? moment(value).format('DD/MM/YYYY HH:mm') : '—'
        },
        { 
            key: "respondente", 
            label: "Respondente",
            thStyle: { width: '220px' },
        },
        { 
            key: "unique_key", 
            label: "CNPJ",
            thStyle: { width: '150px' },
        },
        { 
            key: "numero", 
            label: "Número",
            thStyle: { width: '140px' },
            sortable: true,
        },
        { 
            key: "categoria", 
            label: "Categoria",
            thStyle: { width: '130px' },
            sortable: true,
        },
        { 
            key: "pergunta", 
            label: "Pergunta",
            thStyle: { width: '110px' },
            sortable: true,
        },
        { 
            key: "nota", 
            label: "Nota",
            thStyle: { width: '90px' },
            sortable: true,
        },
        { 
            key: "classificacao", 
            label: "Classificação",
            thStyle: { width: '140px' },
            sortable: true,
        },
        { 
            key: "comment", 
            label: "Comentário",
            thStyle: { width: '280px' },
            sortable: true,
        },
      ],

      respostas: [],

      fieldsLegenda: [
        { key: "codigo", label: "Código" },
        { key: "categoria", label: "Categoria" },
        { key: "descricao", label: "Descrição da Pergunta" },
      ],

      legenda: []
    };
  },

  async created() {
    await this.fetchSurveyCount();
    await this.fetchSurveyAnswers();
    await this.fetchSurveyQuestions();
  },

  watch: {
    async filters() {
        this.currentPage = 1;
        
        await this.fetchSurveyCount();
        await this.fetchSurveyAnswers();
        await this.fetchSurveyQuestions();
    },
    async currentPage() {
        await this.fetchSurveyAnswers();
    },
    async perPage() {
        await this.fetchSurveyAnswers();
    }
  },

  methods: {
    async fetchSurveyCount() {
        const { data } = await axios.get(
            `/reports/survey/count?startDate=${this.filters.startDate}&endDate=${this.filters.endDate}`
        );

        this.nps_general = data.nps_general;
        this.respondentes = data.respondentes;
        this.total_respostas = data.total_respostas;
        this.promotores = data.promotores;
        this.detratores = data.detratores;
    },
    async fetchSurveyAnswers() {
        const { data } = await axios.get('/reports/survey/answers', {
            params: {
                startDate: this.filters.startDate,
                endDate: this.filters.endDate,
                page: this.currentPage,
                perPage: this.perPage
            },
        });

        this.totalRows = data.total;

        this.respostas = data.data.map(resposta => ({
            created_at: resposta.created_at,
            campaign_date_sent: resposta.campaign_date_sent,
            respondente: resposta.wallet_name,
            numero: resposta.tel,
            pergunta: `Q${resposta.question_id}`,
            categoria: resposta.categoria,
            nota: resposta.nota,
            comment: resposta.description,
            unique_key: this.formatCnpjCpf(resposta.unique_key),
        }));
    },
    async fetchSurveyQuestions() {
        const { data } = await axios.get('/reports/survey/questions');

        this.legenda = data.sort((a, b) => a.id - b.id).map(legend => ({
            codigo: `Q${legend.id}`,
            categoria: legend.area,
            descricao: legend.pergunta,
        }));
    },
    async exportExcel() {
      const { data } = await axios.get('/reports/survey/answers', {
        params: {
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          export: true
        }
      });

      const allData = data.data;

      if (!allData.length) return;

      const headers = this.fields.map(f => f.label || f.key);

      const rows = allData.map(item =>
        this.fields.map(field => {
          let value = item[field.key];

          if (field.key === 'classificacao') value = this.classificar(item.nota);
          if (field.key === 'unique_key') value = this.formatCnpjCpf(item.unique_key);
          if ((field.key === 'campaign_date_sent' || field.key === 'created_at') && value) {
            value = moment(value).format('DD/MM/YYYY HH:mm');
          }

          return value ?? '';
        })
      );

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // Ajusta a largura de cada coluna ao maior conte\u00FAdo (com um m\u00EDnimo/m\u00E1ximo)
      worksheet['!cols'] = headers.map((header, colIndex) => {
        const contentWidths = rows.map(row => String(row[colIndex] ?? '').length);
        const maxContent = Math.max(String(header).length, ...contentWidths);

        return { wch: Math.min(Math.max(maxContent + 2, 12), 60) };
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Respostas');

      XLSX.writeFile(workbook, 'respostas_completo.xlsx');
    },
    customFilterAnswer(item, filter) {
      if (!filter) return true;

      const term = filter.toLowerCase();

      const fieldFound = Object.values(item).some(value =>
        String(value).toLowerCase().includes(term)
      );

      const classification = this.classificar(item.nota).toLowerCase();
      const foundClassification = classification.includes(term);

      return fieldFound || foundClassification;
    },
    classificar(nota) {
      if (nota >= 9) return "Promotor";
      if (nota >= 7) return "Neutro";
      return "Detrator";
    },

    badgeVariant(nota) {
      if (nota >= 9) return "primary";
      if (nota >= 7) return "secondary";
      return "danger";
    },

    formatCnpjCpf(value){
        if (!value) return null;
        const cnpjCpf = value.replace(/\D/g, '');

        if (cnpjCpf.length === 11) {
          return cnpjCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, "\$1.\$2.\$3-\$4");
        } 

        return cnpjCpf.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, "\$1.\$2.\$3/\$4-\$5");
    }
  }
};
</script>

<style scoped>
.card-nps {
  background-color: #fdf5e2;
  border: none;
}

.text-nps-passive {
  color: #efaf1a;
}

.text-foreground {
  color: #0f1729;
}

.popover-legend {
  max-width: 500px;
}

.popover-legend :deep(.popover-body) {
  max-height: 100vh;
  overflow-y: auto;
}

/* Tabela de respostas — mais respiro entre linhas/colunas */
.nps-table :deep(th),
.nps-table :deep(td) {
  padding: 14px 16px;
  font-size: 14px;
  white-space: nowrap;
  vertical-align: middle;
}

.nps-table :deep(td) {
  white-space: normal;
}
</style>